import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
  currentEnquiries,
  SHOW_CURRENT_ENQUIRIES,
} from "@/content/site";
import { serviceSections, practicalSupport, serviceIndex } from "@/content/services";
import { activeProjects } from "@/content/commonGround";
import { cfeProjectCard } from "@/content/communityFood";

const WORK_IMAGE = {
  src: "/our-work-farm.jpg",
  alt: "Aerial view of a working farm: crop rows with irrigation channels, solar panel arrays, ponds and a farm shed.",
};

export default function OurWork() {
  return (
    <>
      <Seo
        title="Community Land & Farm Consulting | Bioregional Ventures"
        description="Community land ownership, cooperative farming, regenerative agriculture consulting and ecological restoration planning — research and development for practical, place-based projects."
      />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">Our Work</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight text-charcoal max-w-3xl" data-testid="our-work-heading">
            Research and development for practical, place-based projects.
          </h1>
        </Reveal>
      </section>

      {/* Practical support overview */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal max-w-2xl" data-testid="practical-support-heading">
            {practicalSupport.heading}
          </h2>
          {practicalSupport.intro.map((p) => (
            <p key={p.slice(0, 24)} className="mt-6 first:mt-6 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-3xl">
              {p}
            </p>
          ))}
        </Reveal>
        <Reveal className="mt-12">
          <nav
            aria-label="Service index"
            className="flex flex-wrap gap-x-8 gap-y-3 border-y border-charcoal/10 py-6"
            data-testid="service-index"
          >
            {serviceIndex.map((item, i) =>
              item.href.startsWith("#") ? (
                <a
                  key={item.href}
                  href={item.href}
                  data-testid={`service-index-link-${i + 1}`}
                  className="text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`service-index-link-${i + 1}`}
                  className="text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>
        </Reveal>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7 space-y-14 md:space-y-16">
            {serviceSections.map((s, idx) => (
              <Reveal key={s.id}>
                <article id={s.id} className="scroll-mt-28 border-t-2 border-eucalyptus/25 pt-8" data-testid={`service-section-${s.id}`}>
                  <span className="font-serif text-ochre text-lg">{String(idx + 1).padStart(2, "0")}</span>
                  <h2 className="font-serif text-2xl md:text-3xl leading-snug text-charcoal mt-2">{s.title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-charcoal/85">{s.copy}</p>
                  <p className="mt-6 text-xs uppercase tracking-[0.15em] text-ochre-dark">{s.scopeLabel}</p>
                  <ul className="mt-3 space-y-2 text-sm md:text-base leading-relaxed text-charcoal/85 list-disc pl-5">
                    {s.scope.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>

                  {s.cltExplainer && (
                    <div className="mt-6 bg-sage/60 border border-charcoal/10 p-6" data-testid="clt-explainer">
                      <h3 className="font-serif text-lg text-charcoal">What is a community land trust?</h3>
                      <p className="mt-3 text-sm leading-relaxed text-charcoal/85">{s.cltExplainer}</p>
                      <p className="mt-3 text-xs text-charcoal/60 italic">{s.cltNote}</p>
                      <img
                        src="/community-land-trust-handbook.jpg"
                        alt="Cover of The Community Land Trust Handbook, published by the Institute for Community Economics"
                        data-testid="clt-handbook-image"
                        loading="lazy"
                        className="mt-5 w-full max-w-[220px] border border-charcoal/20"
                      />
                    </div>
                  )}

                  {s.governanceCopy && (
                    <div className="mt-8">
                      <p className="text-base leading-relaxed text-charcoal/85">{s.governanceCopy}</p>
                      <p className="mt-5 text-xs uppercase tracking-[0.15em] text-ochre-dark">{s.governanceScopeLabel}</p>
                      <ul className="mt-3 space-y-2 text-sm md:text-base leading-relaxed text-charcoal/85 list-disc pl-5">
                        {s.governanceScope.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {s.regionalCopy && (
                    <p className="mt-6 text-base leading-relaxed text-charcoal/85 border-l-2 border-ochre pl-4">
                      {s.regionalCopy}
                    </p>
                  )}

                  {s.outcomesCopy && (
                    <div className="mt-8 bg-sage/60 border border-charcoal/10 p-6 md:p-8" data-testid="outcomes-panel">
                      <p className="text-base leading-relaxed text-charcoal/85">{s.outcomesCopy}</p>
                      <h3 className="mt-6 font-serif text-xl text-charcoal">{s.accountabilityHeading}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-charcoal/85">{s.accountabilityCopy}</p>
                      <p className="mt-6 text-xs uppercase tracking-[0.15em] text-ochre-dark">{s.measuresLabel}</p>
                      <ul className="mt-3 space-y-2 text-sm leading-relaxed text-charcoal/85 list-disc pl-5">
                        {s.measures.map((m) => (
                          <li key={m}>{m}</li>
                        ))}
                      </ul>
                      <p className="mt-4 text-xs text-charcoal/60">{s.measuresNote}</p>
                      <p className="mt-6 text-sm">
                        <Link
                          to="/resources"
                          data-testid="services-resources-link"
                          className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
                        >
                          Further reading in our Resources list
                        </Link>
                      </p>
                    </div>
                  )}

                  {s.note && (
                    <p className="mt-5 text-xs leading-relaxed text-charcoal/60 border-l-2 border-ochre pl-4">{s.note}</p>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="md:col-span-5" delay={0.1}>
            <div className="md:sticky md:top-28">
              <div className="overflow-hidden rounded-sm">
                <img
                  src={WORK_IMAGE.src}
                  alt={WORK_IMAGE.alt}
                  data-testid="our-work-image"
                  loading="lazy"
                  className="w-full h-72 md:h-[480px] object-cover hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <p className="mt-4 text-xs text-charcoal/60 italic">
                Photo: community farm
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Project development approach link */}
      <section className="bg-eucalyptus text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-16">
          <Reveal>
            <Link
              to="/approach"
              data-testid="our-work-approach-link"
              className="font-serif text-2xl md:text-3xl tracking-tight leading-snug underline decoration-ochre underline-offset-8 hover:text-ochre transition-colors duration-300"
            >
              See our project development approach
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Active projects */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="active-projects-section">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-6" data-testid="active-projects-eyebrow">
            {activeProjects.eyebrow}
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...activeProjects.projects, cfeProjectCard].map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06} className="h-full">
              <Link
                to={p.href}
                data-testid={`active-project-card-${i + 1}`}
                className="group block h-full border border-charcoal/15 bg-cream overflow-hidden hover:border-ochre/60 transition-colors duration-300"
              >
                <div className="overflow-hidden">
                  <img
                    src={p.image.src}
                    alt={p.image.alt}
                    loading="lazy"
                    className="w-full h-56 md:h-64 object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="p-7">
                  <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">{p.status}</p>
                  <h3 className="mt-2 font-serif text-2xl text-charcoal">{p.name}</h3>
                  <p className="mt-1 font-serif italic text-lg text-charcoal/80">{p.tagline}</p>
                  <p className="mt-4 text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 group-hover:text-charcoal transition-colors duration-300">
                    {p.ctaLabel || "Explore the project"}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {SHOW_CURRENT_ENQUIRIES && (
        <section className="bg-sage/60 border-y border-charcoal/10" data-testid="current-enquiries-section">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">Current enquiries</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal max-w-2xl">
                Themes we are actively exploring
              </h2>
              <p className="mt-6 text-sm md:text-base text-charcoal/80 max-w-2xl border-l-2 border-ochre pl-4" data-testid="enquiries-disclaimer">
                {currentEnquiries.label}
              </p>
              <ul className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                {currentEnquiries.items.map((item, i) => (
                  <li
                    key={item}
                    className="border border-charcoal/15 bg-cream p-7 text-base leading-relaxed text-charcoal/90"
                    data-testid={`enquiry-theme-${i + 1}`}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
        <Reveal>
          <p className="font-serif text-2xl md:text-3xl leading-snug text-charcoal max-w-xl mx-auto">
            Have a place or a project in mind?
          </p>
          <Link
            to="/contact"
            data-testid="our-work-cta-button"
            className="mt-8 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            Discuss your project
          </Link>
        </Reveal>
      </section>
    </>
  );
}
